import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    Paper,
    Grid,
    Chip,
    Button,
    Alert,
    List,
    ListItem,
    ListItemText,
    Accordion,
    AccordionSummary,
    AccordionDetails
} from "@mui/material";
import { ExpandMore, Refresh, BugReport } from "@mui/icons-material";
import { useAuth } from "../context/AuthContext";
import { 
    hasPermission, 
    hasBusinessAccess, 
    hasSystemAccess, 
    getUserPermissions,
    PERMISSIONS 
} from "../utils/permissions";

const AdminDebugPanel = () => {
    const { currentUser, userProfile, loading, refreshUserData } = useAuth();
    const [debugInfo, setDebugInfo] = useState({});
    const [expanded, setExpanded] = useState("user");

    const updateDebugInfo = useCallback(() => {
        if (!currentUser || !userProfile) {
            setDebugInfo({
                user: null,
                profile: null,
                permissions: [],
                accessChecks: {}
            });
            return;
        }

        const permissions = getUserPermissions(userProfile);
        const accessChecks = {
            business: hasBusinessAccess(userProfile),
            system: hasSystemAccess(userProfile),
            audit: hasPermission(userProfile, PERMISSIONS.AUDIT.VIEW_AUDIT_LOGS)
        };

        setDebugInfo({
            user: currentUser,
            profile: userProfile,
            permissions,
            accessChecks
        });
    }, [currentUser, userProfile]);

    useEffect(() => {
        updateDebugInfo();
    }, [updateDebugInfo]);

    const handleRefresh = async () => {
        try {
            await refreshUserData();
            updateDebugInfo();
        } catch (error) {
            // Error refreshing user data
        }
    };

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    if (loading) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>Cargando información de debug...</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
            <Paper sx={{ p: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <BugReport sx={{ mr: 1 }} />
                    <Typography variant="h4" component="h1">
                        Panel de Diagnóstico de Administración
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<Refresh />}
                        onClick={handleRefresh}
                        sx={{ ml: "auto" }}
                    >
                        Refrescar Datos
                    </Button>
                </Box>

                {!currentUser && (
                    <Alert severity="warning" sx={{ mb: 3 }}>
                        No hay usuario autenticado
                    </Alert>
                )}

                {!userProfile && currentUser && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                        Usuario autenticado pero sin perfil cargado
                    </Alert>
                )}

                {/* Información del Usuario */}
                <Accordion expanded={expanded === "user"} onChange={handleAccordionChange("user")}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="h6">Información del Usuario</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Firebase User
                                </Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemText 
                                            primary="UID" 
                                            secondary={debugInfo.user?.uid || "N/A"} 
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText 
                                            primary="Email" 
                                            secondary={debugInfo.user?.email || "N/A"} 
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText 
                                            primary="Email Verified" 
                                            secondary={debugInfo.user?.emailVerified ? "Sí" : "No"} 
                                        />
                                    </ListItem>
                                </List>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    User Profile
                                </Typography>
                                <List dense>
                                    <ListItem>
                                        <ListItemText 
                                            primary="Role" 
                                            secondary={
                                                <Chip 
                                                    label={debugInfo.profile?.role || "N/A"} 
                                                    color={debugInfo.profile?.role === "SUPER_ADMIN" ? "error" : "default"}
                                                    size="small"
                                                />
                                            } 
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText 
                                            primary="Tier" 
                                            secondary={debugInfo.profile?.tier || "N/A"} 
                                        />
                                    </ListItem>
                                    <ListItem>
                                        <ListItemText 
                                            primary="Balance" 
                                            secondary={`€${debugInfo.profile?.balance || 0}`} 
                                        />
                                    </ListItem>
                                </List>
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>

                {/* Verificaciones de Acceso */}
                <Accordion expanded={expanded === "access"} onChange={handleAccordionChange("access")}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="h6">Verificaciones de Acceso</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={4}>
                                <Paper sx={{ p: 2, textAlign: "center" }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Acceso Business Admin
                                    </Typography>
                                    <Chip 
                                        label={debugInfo.accessChecks?.business ? "PERMITIDO" : "DENEGADO"}
                                        color={debugInfo.accessChecks?.business ? "success" : "error"}
                                        sx={{ mb: 1 }}
                                    />
                                    <Typography variant="caption" display="block">
                                        /admin/business
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Paper sx={{ p: 2, textAlign: "center" }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Acceso System Admin
                                    </Typography>
                                    <Chip 
                                        label={debugInfo.accessChecks?.system ? "PERMITIDO" : "DENEGADO"}
                                        color={debugInfo.accessChecks?.system ? "success" : "error"}
                                        sx={{ mb: 1 }}
                                    />
                                    <Typography variant="caption" display="block">
                                        /admin/system
                                    </Typography>
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={4}>
                                <Paper sx={{ p: 2, textAlign: "center" }}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Acceso Audit
                                    </Typography>
                                    <Chip 
                                        label={debugInfo.accessChecks?.audit ? "PERMITIDO" : "DENEGADO"}
                                        color={debugInfo.accessChecks?.audit ? "success" : "error"}
                                        sx={{ mb: 1 }}
                                    />
                                    <Typography variant="caption" display="block">
                                        audit:view_logs
                                    </Typography>
                                </Paper>
                            </Grid>
                        </Grid>
                    </AccordionDetails>
                </Accordion>

                {/* Permisos del Usuario */}
                <Accordion expanded={expanded === "permissions"} onChange={handleAccordionChange("permissions")}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="h6">
                            Permisos del Usuario ({debugInfo.permissions?.length || 0})
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Box sx={{ maxHeight: 400, overflow: "auto" }}>
                            <Grid container spacing={1}>
                                {debugInfo.permissions?.map((permission, index) => (
                                    <Grid item xs={12} sm={6} md={4} key={index}>
                                        <Chip 
                                            label={permission} 
                                            size="small" 
                                            variant="outlined"
                                            sx={{ mb: 1 }}
                                        />
                                    </Grid>
                                ))}
                                {(!debugInfo.permissions || debugInfo.permissions.length === 0) && (
                                    <Grid item xs={12}>
                                        <Typography color="text.secondary">
                                            No se encontraron permisos
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    </AccordionDetails>
                </Accordion>

                {/* Información de Debug */}
                <Accordion expanded={expanded === "debug"} onChange={handleAccordionChange("debug")}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="h6">Información de Debug</Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Typography variant="subtitle2" gutterBottom>
                            Datos Raw del Perfil
                        </Typography>
                        <Paper sx={{ p: 2, bgcolor: "grey.50", fontFamily: "monospace", fontSize: "0.8rem" }}>
                            <pre>{JSON.stringify(debugInfo.profile, null, 2)}</pre>
                        </Paper>
                    </AccordionDetails>
                </Accordion>

                {/* Acciones de Debug */}
                <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: "divider" }}>
                    <Typography variant="h6" gutterBottom>
                        Acciones de Debug
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item>
                            <Button 
                                variant="contained" 
                                color="primary"
                                onClick={handleRefresh}
                            >
                                Refrescar Perfil de Usuario
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button 
                                variant="outlined"
                                onClick={() => {
                                    // Debug info logged to console
                                }}
                            >
                                Log a Consola
                            </Button>
                        </Grid>
                    </Grid>
                </Box>
            </Paper>
        </Box>
    );
};

export default AdminDebugPanel; 